import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RevoLogo } from '../components/RevoLogo';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { isPlatformAuthenticatorAvailable, loginWithPasskey } from '../utils/webauthn';
import { Eye, EyeOff, ArrowLeft, Mail, KeyRound, ShieldCheck, ArrowUpRight, ShieldCheck as Shield, Fingerprint } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const HERO_DEFAULT = '/marketing/login-hero.jpg';

const CardShell = ({ children, testid }) => (
  <div className="relative w-full bg-white rounded-2xl border border-slate-200/80 shadow-[0_16px_44px_-14px_rgba(15,23,42,0.14)] overflow-hidden" data-testid={testid}>
    <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-400" />
    <div className="p-6 sm:p-8">{children}</div>
  </div>
);

const BRANDS = [
  { id: 'revoquota', name: 'RevoQuota', tag: 'Linee Vita & Sicurezza', url: 'https://revoquota.com', logo: '/product-logos/revoquota.png', dark: false, testid: 'brand-chip-revoquota' },
  { id: 'isoliamo', name: 'Isoliamo', tag: 'Isolamento Termico', url: 'https://isoliamo.com', logo: '/product-logos/isoliamo.png', dark: false, testid: 'brand-chip-isoliamo' },
  { id: 'evoluzione-tetto', name: 'Evoluzione Tetto', tag: 'Rifacimento Tetti', url: 'https://evoluzionetetto.com', logo: '/product-logos/evoluzione-tetto.png', dark: false, testid: 'brand-chip-evoluzione-tetto' },
  { id: 'revoultra', name: 'RevoUltra', tag: 'Guaina Nanopolimerica', url: 'https://revoultra.it', logo: '/product-logos/revoultra.png', dark: true, testid: 'brand-chip-revoultra' },
];

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [heroUrl, setHeroUrl] = useState(HERO_DEFAULT);

  const [recoveryMode, setRecoveryMode] = useState(null); // null | 'email' | 'otp'
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [remember, setRemember] = useState(true);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  useEffect(() => {
    isPlatformAuthenticatorAvailable().then(setBiometricAvailable).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/public/login-hero`);
        if (res.ok) {
          const d = await res.json();
          if (d.configured && d.image_url) setHeroUrl(`${BACKEND_URL}${d.image_url}`);
        }
      } catch (e) { /* usa default */ }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.login(email, password, remember);
      toast.success('Login effettuato con successo!');
      onLogin(response.user, response.access_token, remember);
    } catch (error) {
      toast.error('Login fallito. Verifica email e password.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const response = await loginWithPasskey(email.trim(), remember);
      toast.success('Accesso biometrico riuscito!');
      onLogin(response.user, response.access_token, remember);
    } catch (error) {
      const msg = (error && error.name === 'NotAllowedError')
        ? 'Riconoscimento annullato o non riuscito.'
        : 'Nessuna passkey su questo dispositivo. Accedi con password e attivala dal menu.';
      toast.error(msg);
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!recoveryEmail) { toast.error('Inserisci la tua email'); return; }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail })
      });
      const data = await res.json();
      if (res.ok) { toast.success('Se l\'email esiste, riceverai un codice OTP.'); setRecoveryMode('otp'); }
      else toast.error(data.detail || 'Errore durante la richiesta');
    } catch (err) { toast.error('Errore di connessione'); }
    finally { setRecoveryLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) { toast.error('Inserisci il codice OTP a 6 cifre'); return; }
    if (!newPassword || newPassword.length < 6) { toast.error('La password deve avere almeno 6 caratteri'); return; }
    if (newPassword !== confirmPassword) { toast.error('Le password non corrispondono'); return; }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail, otp, new_password: newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password reimpostata con successo!');
        setRecoveryMode(null); setEmail(recoveryEmail); setRecoveryEmail('');
        setOtp(''); setNewPassword(''); setConfirmPassword('');
      } else toast.error(data.detail || 'Errore durante il reset');
    } catch (err) { toast.error('Errore di connessione'); }
    finally { setRecoveryLoading(false); }
  };

  const handleBackToLogin = () => { setRecoveryMode(null); setOtp(''); setNewPassword(''); setConfirmPassword(''); };

  const fade = () => ({});

  const inputCls = 'h-11 rounded-lg border-slate-300 bg-white px-3.5 text-sm shadow-sm transition-all duration-150 focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:border-emerald-600';
  const primaryBtn = 'w-full h-11 rounded-lg bg-emerald-700 hover:bg-emerald-800 active:scale-[0.99] text-white font-medium text-sm tracking-wide shadow-md shadow-emerald-900/15 transition-all duration-150';

  const renderLoginForm = () => (
    <CardShell testid="login-card">
      <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Accedi al tuo account</h1>
      <p className="text-xs sm:text-sm text-slate-500 mt-1">Inserisci le tue credenziali per accedere alla piattaforma.</p>
      <form onSubmit={handleSubmit} data-testid="login-form" className="space-y-4 mt-6">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Email</Label>
          <Input id="email" name="email" type="email" autoComplete="username" placeholder="nome@esempio.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className={inputCls} />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Password</Label>
            <button type="button" onClick={() => { setRecoveryMode('email'); setRecoveryEmail(email); }} className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline transition-colors" data-testid="forgot-password-link">Password dimenticata?</button>
          </div>
          <div className="relative">
            <Input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder={'\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022'} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className={`${inputCls} pr-11`} />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-0 h-full px-3.5 flex items-center text-slate-400 hover:text-slate-700 transition-colors rounded-r-lg" data-testid="toggle-password-visibility" tabIndex={-1} aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}>
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-0.5">
          <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" className="border-slate-300 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
          <Label htmlFor="remember" className="text-xs sm:text-sm text-slate-600 font-medium cursor-pointer select-none">Ricordami su questo dispositivo</Label>
        </div>
        <Button type="submit" className={primaryBtn} disabled={loading} data-testid="login-submit-button">
          {loading ? 'Accesso in corso...' : 'Accedi'}
        </Button>
        {biometricAvailable && (
          <>
            <div className="flex items-center gap-3 pt-1">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">oppure</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <button
              type="button"
              onClick={handlePasskeyLogin}
              disabled={passkeyLoading}
              data-testid="passkey-login-button"
              className="w-full h-11 rounded-lg border border-emerald-600/60 bg-emerald-50/60 hover:bg-emerald-100/70 active:scale-[0.99] text-emerald-800 font-medium text-sm tracking-wide flex items-center justify-center gap-2 transition-all duration-150 disabled:opacity-60"
            >
              <Fingerprint className="w-5 h-5" />
              {passkeyLoading ? 'Verifica in corso...' : 'Accedi con Face ID / impronta'}
            </button>
          </>
        )}
      </form>
    </CardShell>
  );

  const renderRecoveryEmailStep = () => (
    <CardShell testid="recovery-email-card">
      <div className="flex items-center gap-2 mb-1">
        <button onClick={handleBackToLogin} className="text-slate-500 hover:text-slate-900 transition-colors p-1 rounded-md hover:bg-slate-100" data-testid="back-to-login-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Recupero credenziali</h1>
          <p className="text-xs text-slate-500 mt-0.5">Inserisci l'email aziendale per ricevere il codice OTP.</p>
        </div>
      </div>
      <form onSubmit={handleRequestOtp} className="space-y-4 mt-5" data-testid="recovery-email-form">
        <div className="space-y-1.5">
          <Label htmlFor="recovery-email" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input id="recovery-email" name="email" type="email" autoComplete="username" placeholder="nome@esempio.com" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} required data-testid="recovery-email-input" className={`${inputCls} pl-10`} />
          </div>
        </div>
        <Button type="submit" className={primaryBtn} disabled={recoveryLoading} data-testid="send-otp-btn">{recoveryLoading ? 'Invio in corso...' : 'Invia codice di verifica'}</Button>
      </form>
    </CardShell>
  );

  const renderRecoveryOtpStep = () => (
    <CardShell testid="reset-password-card">
      <div className="flex items-center gap-2 mb-1">
        <button onClick={() => setRecoveryMode('email')} className="text-slate-500 hover:text-slate-900 transition-colors p-1 rounded-md hover:bg-slate-100" data-testid="back-to-email-step-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-lg sm:text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Verifica e nuova password</h1>
          <p className="text-xs text-slate-500 mt-0.5">Inserisci il codice a 6 cifre e la nuova password.</p>
        </div>
      </div>
      <form onSubmit={handleResetPassword} className="space-y-4 mt-5" data-testid="reset-password-form">
        <div className="space-y-1.5">
          <Label htmlFor="otp-code" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Codice OTP</Label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
            <Input id="otp-code" type="text" inputMode="numeric" maxLength={6} placeholder="123456" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} required data-testid="otp-input" className="h-12 pl-10 text-center text-xl tracking-[0.4em] font-mono font-bold rounded-lg border-slate-300 focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:border-emerald-600 bg-slate-50/60" />
          </div>
          <p className="text-[11px] text-slate-500">Controlla la casella email di <strong>{recoveryEmail}</strong></p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Nuova password</Label>
          <div className="relative">
            <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input id="new-password" name="new-password" type={showNewPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Minimo 6 caratteri" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="new-password-input" className={`${inputCls} pl-10 pr-11`} />
            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-0 top-0 h-full px-3.5 flex items-center text-slate-400 hover:text-slate-700 transition-colors rounded-r-lg" data-testid="toggle-new-password-visibility" tabIndex={-1}>{showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider text-slate-700">Conferma password</Label>
          <div className="relative">
            <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input id="confirm-password" name="confirm-password" type={showNewPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Ripeti la password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="confirm-password-input" className={`${inputCls} pl-10`} />
          </div>
          {confirmPassword && newPassword !== confirmPassword && (<p className="text-[11px] text-destructive">Le password non corrispondono</p>)}
        </div>
        <Button type="submit" className={primaryBtn} disabled={recoveryLoading || otp.length !== 6 || !newPassword || newPassword !== confirmPassword} data-testid="reset-password-btn">{recoveryLoading ? 'Reimpostazione...' : 'Salva nuova password e accedi'}</Button>
      </form>
    </CardShell>
  );

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row bg-[#0F172A] selection:bg-teal-500/30" data-testid="login-page">
      {/* Left panel */}
      <div className="w-full lg:w-[48%] xl:w-[44%] min-h-screen bg-slate-50 flex flex-col justify-between p-6 sm:p-10 lg:p-12 relative z-20 border-r border-slate-200/80 overflow-y-auto">
        <motion.div {...fade(0)} className="flex items-center justify-between">
          <RevoLogo size="lg" />
          <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] font-mono font-semibold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200/70 rounded-full px-2.5 py-1">
            <Shield className="w-3 h-3" /> Area riservata
          </span>
        </motion.div>

        <div className="w-full max-w-sm mx-auto py-8">
          <motion.div {...fade(0.08)}>
            {recoveryMode === null && renderLoginForm()}
            {recoveryMode === 'email' && renderRecoveryEmailStep()}
            {recoveryMode === 'otp' && renderRecoveryOtpStep()}
          </motion.div>

          <motion.div {...fade(0.16)} className="mt-7">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px flex-1 bg-slate-200" />
              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-mono font-medium">I nostri marchi</p>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
              {BRANDS.map((b, i) => (
                <motion.a
                  key={b.id}
                  href={b.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={b.testid}
                  {...fade(0.2 + i * 0.05)}
                  className="relative group flex items-center gap-3 p-2.5 sm:p-3 rounded-xl bg-white border border-slate-200/90 shadow-sm hover:border-emerald-600/70 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 shrink-0 rounded-lg flex items-center justify-center p-1.5 transition-transform duration-200 group-hover:scale-105 ${b.dark ? 'bg-[#090D16] border border-slate-700/60 shadow-inner' : 'bg-white border border-slate-100'}`}>
                    <img src={b.logo} alt={b.name} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-semibold tracking-tight text-slate-800 group-hover:text-emerald-700 leading-none truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{b.name}</p>
                    <p className="text-[9px] uppercase tracking-wider text-slate-500 font-mono font-medium leading-tight mt-1 truncate">{b.tag}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-all duration-200 shrink-0" />
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.p {...fade(0.3)} className="text-[11px] text-center text-slate-400">
          © {new Date().getFullYear()} Gruppo RevoGreen · Piattaforma CRM
        </motion.p>
      </div>

      {/* Right hero panel */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[56%] relative overflow-hidden flex-col justify-between p-12 xl:p-16 text-white min-h-screen bg-slate-900 select-none">
        <img src={heroUrl} alt="Coperture RevoGreen" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { if (e.currentTarget.src !== HERO_DEFAULT) e.currentTarget.src = HERO_DEFAULT; }} />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950/95 via-teal-950/75 to-slate-900/50" />

        <motion.div {...fade(0.1)} className="relative z-10 flex items-center gap-2">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-emerald-200/90 bg-white/10 border border-white/15 backdrop-blur-md rounded-full px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Gruppo RevoGreen
          </span>
        </motion.div>

        <motion.div {...fade(0.2)} className="relative z-10 max-w-xl">
          <h2 className="text-3xl sm:text-4xl lg:text-[2.65rem] font-bold tracking-tight leading-[1.15]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Insieme, costruiamo il futuro
          </h2>
          <p className="text-sm sm:text-base text-emerald-100/85 leading-relaxed mt-4 max-w-md">
            Ogni tetto protetto, ogni edificio efficiente: la piattaforma intelligente per la rete tecnica del Gruppo RevoGreen.
          </p>
        </motion.div>

        <div className="relative z-10" />
      </div>
    </div>
  );
};

export default Login;
